import React, { useMemo, useState } from 'react';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import Tether from '../components/Tether';
import PopOverContainer from '../components/deprecated/PopOverContainer';
import CheckRadioControl from '../components/formInternals/CheckRadioControl';
import filterIcon from '../icons/non-prefilled/16x16/filter.svg';
import LocalSearchHighlight from '../search/LocalSearchHighlight';
import localEntitySearch, {
  TopicMatchKey,
} from '../search/entitySearch/localEntitySearch';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import useShortcut from '../utils/useShortcut';
import * as styles from './ContentMapFilter.module.scss';
import type { TopicReorganizationTree } from './useTopicReorganization';

interface ContentMapFilterProps {
  columnTrees: TopicReorganizationTree[];
  selectedIds: Set<string>;
  onSelectedIdsChange: (newSelectedIds: Set<string>) => void;
}

const ContentMapFilter = ({
  columnTrees,
  selectedIds,
  onSelectedIdsChange,
}: ContentMapFilterProps) => {
  const [isActive, setIsActive] = useState(false);
  const [value, setValue] = useState('');

  const close = () => {
    setIsActive(false);
    setValue('');
  };

  useShortcut(
    'esc',
    (e) => {
      e.preventDefault();
      if (isActive) close();
    },
    { global: true, skip: !isActive },
  );

  const topics = useMemo(
    () =>
      columnTrees.map(({ topic, reorganizationMetadata }) =>
        reorganizationMetadata.name
          ? { ...topic, name: reorganizationMetadata.name }
          : topic,
      ),
    [columnTrees],
  );

  const searchResults = localEntitySearch(value, {
    topic: { objects: topics },
  });

  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  return (
    <div className={styles.container}>
      {isActive ? (
        <ClickOutsideHandler onClickOutside={close}>
          <div ref={setAnchorElement}>
            <SVGIcon className={styles.icon} size="small" inline>
              {filterIcon}
            </SVGIcon>
            <input
              className={styles.input}
              placeholder="Filter by topic"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              aria-label="Filter by topic"
            />
          </div>
          <Tether
            placement="bottom-start"
            anchorElement={anchorElement}
            positionedView={
              <div className={styles.topics}>
                <PopOverContainer>
                  {searchResults.length > 0 ? (
                    searchResults.map((result) => {
                      if (result.type !== 'topic') return null;
                      const { topic, matches } = result;
                      return (
                        <div key={topic.id} className={styles.topic}>
                          <CheckRadioControl
                            type="checkbox"
                            label={
                              <div>
                                <LocalSearchHighlight
                                  matches={matches}
                                  matchKey={TopicMatchKey.Name}
                                  defaultValue={topic.name}
                                />
                              </div>
                            }
                            onChange={(e) => {
                              const newSelectedIds = new Set(selectedIds);
                              if (e.target.checked) {
                                newSelectedIds.add(topic.id);
                              } else {
                                newSelectedIds.delete(topic.id);
                              }
                              onSelectedIdsChange(newSelectedIds);
                            }}
                            checked={selectedIds.has(topic.id)}
                          />
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.empty}>No matches</div>
                  )}
                </PopOverContainer>
              </div>
            }
          />
        </ClickOutsideHandler>
      ) : (
        <>
          <Button
            variant="secondary"
            size="small"
            icon={filterIcon}
            onClick={() => setIsActive(true)}
          >
            Filter
            {selectedIds.size > 0 && ` (${selectedIds.size})`}
          </Button>
          {selectedIds.size > 0 && (
            <div className={styles.clear}>
              <Button
                variant="link"
                size="small"
                onClick={() => onSelectedIdsChange(new Set())}
              >
                Clear all
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContentMapFilter;
